import { salesOrderApi } from './api/sales-order-api'

const VAPID_PUBLIC_KEY: string = import.meta.env.VITE_VAPID_PUBLIC_KEY || ''

const log = (...args: unknown[]) => console.log('[Push]', ...args)
const logErr = (...args: unknown[]) => console.error('[Push]', ...args)

function urlB64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/')
    const raw = atob(base64)
    const arr = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
    return arr
}

/** Check if the browser supports Web Push */
export function isPushSupported(): boolean {
    const supported =
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        !!VAPID_PUBLIC_KEY
    if (!supported) {
        log('not supported:', {
            sw: 'serviceWorker' in navigator,
            push: 'PushManager' in window,
            vapid: !!VAPID_PUBLIC_KEY,
        })
    }
    return supported
}

/** Get current notification permission state */
export function getPushPermission(): NotificationPermission {
    return Notification.permission
}

/**
 * Đăng ký subscription này với TÀI KHOẢN ĐANG ĐĂNG NHẬP.
 *
 * Subscription thuộc về trình duyệt chứ không thuộc tài khoản, nên cùng một máy
 * đăng nhập tài khoản khác thì server vẫn ghi endpoint đó cho chủ cũ. Gửi lại là
 * idempotent: `Upsert ... ON CONFLICT (endpoint) DO UPDATE SET user_id` ở BE sẽ
 * chuyển quyền sở hữu sang người đang đăng nhập.
 */
async function registerWithServer(sub: PushSubscription): Promise<void> {
    const json = sub.toJSON()
    await salesOrderApi.subscribePush({
        endpoint: json.endpoint!,
        p256dh: json.keys!.p256dh,
        auth: json.keys!.auth,
    })
}

/** Request push permission and subscribe. Returns true on success. */
export async function subscribeToPush(): Promise<boolean> {
    if (!isPushSupported()) return false

    log('requesting permission...')
    const permission = await Notification.requestPermission()
    log('permission:', permission)
    if (permission !== 'granted') return false

    log('waiting for SW ready...')
    const reg = await navigator.serviceWorker.ready
    log('SW ready, subscribing to push...')

    const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC_KEY),
    })
    log('push subscription created:', sub.endpoint.slice(0, 60))

    await registerWithServer(sub)
    log('subscription sent to server')

    return true
}

/** Unsubscribe from push notifications */
export async function unsubscribeFromPush(): Promise<void> {
    // Không dùng `serviceWorker.ready`: khi trình duyệt không có service worker
    // đang chạy (HTTPS chứng chỉ tự ký, đăng ký SW lỗi…) promise đó không bao
    // giờ resolve và `logout()` treo vĩnh viễn — nút Đăng xuất "bấm không ăn".
    // `getRegistration()` trả undefined ngay khi chưa có SW: không có SW thì
    // cũng không có subscription nào để huỷ.
    if (!('serviceWorker' in navigator)) return
    const reg = await navigator.serviceWorker.getRegistration()
    if (!reg) return
    const sub = await reg.pushManager.getSubscription()
    if (sub) {
        await salesOrderApi.unsubscribePush(sub.endpoint)
        await sub.unsubscribe()
        log('unsubscribed')
    }
}

/** Check if currently subscribed to push */
export async function isSubscribed(): Promise<boolean> {
    if (!isPushSupported()) return false
    try {
        const reg = await navigator.serviceWorker.ready
        const sub = await reg.pushManager.getSubscription()
        const result = sub !== null
        log('isSubscribed:', result)
        return result
    } catch (err) {
        logErr('isSubscribed check failed:', err)
        return false
    }
}

/**
 * Đồng bộ push cho tài khoản đang đăng nhập. Chạy mỗi lần vào app (qua
 * `usePushAutoPrompt`) nên là chỗ duy nhất bảo đảm server biết đúng chủ.
 *
 * Hai trường hợp:
 *  - Chưa có subscription (service worker vừa cài lại sau deploy) → đăng ký mới.
 *  - ĐÃ có subscription → vẫn gửi lại lên server. Bỏ qua bước này chính là bug:
 *    admin bật push rồi đăng xuất, hộ trồng đăng nhập trên cùng máy thì trình
 *    duyệt vẫn còn subscription cũ nên chuông hiện "đã bật", nhưng server vẫn
 *    gắn endpoint cho admin → hộ không nhận được thông báo nào.
 */
export async function ensureSubscription(): Promise<void> {
    if (!isPushSupported()) return
    if (Notification.permission !== 'granted') return

    try {
        const reg = await navigator.serviceWorker.ready
        const sub = await reg.pushManager.getSubscription()
        if (!sub) {
            log('permission granted but no subscription, re-subscribing...')
            await subscribeToPush()
            return
        }

        await registerWithServer(sub)
        log('subscription re-registered for the current account')
    } catch (err) {
        logErr('ensureSubscription failed:', err)
    }
}
